import { createAsyncThunk, createAction, isRejected } from '@reduxjs/toolkit';
import {
  actions as appStorageActions,
  constants as appStorageConstants,
  records as appStorageRecords,
  utils as appStorageUtils,
} from '@saucelabs/sl-module-app-storage';

import types from './types';
import selectors from './selectors';
import { GlobalState } from '../../reducers';
import { ValueOf } from '../../GlobalTypes';

export type FetchGroupsArguments = {
  paginationPage?: number;
  entriesPerPage?: number;
  osFilters?: ValueOf<typeof appStorageConstants.APP_STORAGE_KIND>[];
  searchQuery?: string;
}

const setCurrentGroup = createAction<appStorageRecords.AppStorageGroup | undefined>(
  types.SET_CURRENT_GROUP,
);

const fetchGroups = createAsyncThunk(
  types.FETCH_GROUPS,
  async (payload: FetchGroupsArguments = {}, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as GlobalState;
    const previousFetchGroupsArguments = selectors.fetchGroupsArgumentsSelector(state);

    const nextFetchGroupsArguments = {
      iconRepresentation: appStorageConstants.APP_STORAGE_ICON_REPRESENTATION.hash,
      ...previousFetchGroupsArguments,
      ...payload,
    };

    try {
      const globalFetchGroups = await dispatch(
        appStorageActions.fetchGroups(nextFetchGroupsArguments),
      );

      if (isRejected(globalFetchGroups)) {
        const serverError = globalFetchGroups.payload as appStorageUtils.SerializedApiError;
        throw serverError;
      }

      return globalFetchGroups.payload;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(appStorageUtils.handleApiError(error));
      }

      return rejectWithValue(error);
    }
  },
);

const fetchGroup = createAsyncThunk(
  types.FETCH_GROUP,
  async (groupId: string, { dispatch, rejectWithValue }) => {
    try {
      const fetchGroupResult = await dispatch(
        appStorageActions.fetchGroups({
          groupId,
          iconRepresentation: appStorageConstants.APP_STORAGE_ICON_REPRESENTATION.hash,
        }),
      );

      if (isRejected(fetchGroupResult)) {
        const serverError = fetchGroupResult.payload as appStorageUtils.SerializedApiError;
        throw serverError;
      }

      return fetchGroupResult.payload;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(appStorageUtils.handleApiError(error));
      }

      return rejectWithValue(error);
    }
  },
);

export default {
  fetchGroup,
  fetchGroups,
  setCurrentGroup,
};
